<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import { AMOUNT_PRESETS, AMOUNT_STEP } from '../constants/fiatWalletConstants'

const MAX_DECIMAL_DIGITS = 2

interface Props {
  modelValue: number
  /** Currency prefix rendered inside the field. */
  currency?: string
  /** Quick-pick amounts under the field. Pass `[]` to hide the row. */
  presets?: readonly number[]
  /** Show the up / down stepper on the right of the field (withdraw modal). */
  stepper?: boolean
  step?: number
  min?: number
  /** Upper bound. Presets above it render disabled; see `clamp` for typed input. */
  max?: number
  /**
   * Clamp typed input to `max`. The withdraw flow turns this off so an
   * over-balance amount can be entered and answered with an inline error —
   * silently rewriting what someone typed on a money field is worse than
   * telling them it's too much.
   */
  clamp?: boolean
  invalid?: boolean
  /** Inline message shown between the field and the presets. */
  error?: string
}

const props = withDefaults(defineProps<Props>(), {
  currency: 'AED',
  presets: () => AMOUNT_PRESETS,
  stepper: false,
  step: AMOUNT_STEP,
  min: 0,
  max: Number.POSITIVE_INFINITY,
  clamp: true,
  invalid: false,
  error: '',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

function formatNumber(amount: number): string {
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: MAX_DECIMAL_DIGITS,
  })
}

function applyBounds(value: number): number {
  if (Number.isNaN(value)) return props.min
  const lowerBounded = Math.max(value, props.min)
  return props.clamp ? Math.min(lowerBounded, props.max) : lowerBounded
}

// Mirrors the model as a formatted string, except while the field has focus —
// re-formatting mid-keystroke would fight the caret position.
const inputValue = ref(formatNumber(props.modelValue))
const typing = ref(false)

watch(
  () => props.modelValue,
  (value) => {
    if (!typing.value) inputValue.value = formatNumber(value)
  },
)

watch(
  () => props.max,
  (newMax) => {
    if (props.clamp && props.modelValue > newMax) {
      const clamped = applyBounds(props.modelValue)
      emit('update:modelValue', clamped)
      if (!typing.value) inputValue.value = formatNumber(clamped)
    }
  },
)

const setAmount = (value: number) => {
  const clamped = applyBounds(value)
  emit('update:modelValue', clamped)
  inputValue.value = formatNumber(clamped)
}

const increment = () => setAmount(props.modelValue + props.step)
const decrement = () => setAmount(Math.max(props.min, props.modelValue - props.step))

const onInput = (event: Event) => {
  typing.value = true
  const target = event.target as HTMLInputElement
  const parsed = parseFloat(target.value.replace(/,/g, ''))
  if (Number.isNaN(parsed)) {
    inputValue.value = target.value
    emit('update:modelValue', 0)
    return
  }
  const clamped = applyBounds(parsed)
  emit('update:modelValue', clamped)
  // Show the cap immediately when a typed value got clamped.
  inputValue.value = clamped !== parsed ? formatNumber(clamped) : target.value
}

const onBlur = () => {
  typing.value = false
  inputValue.value = formatNumber(props.modelValue)
}

const isPresetDisabled = (preset: number) => preset > props.max
</script>

<template>
  <Box direction="col" gap="2" class="amt">
    <Box align="center" gap="3" :class="['amt__box', { 'amt__box--invalid': invalid }]">
      <Text type="title-lg" weight="medium" color="muted" is="span" class="amt__currency">
        <CurrencyText :text="currency" />
      </Text>

      <!-- Native <input> with the design's 20px display type — no kit numeric
           input exists yet; same call-site exception the kit allows today. -->
      <input
        :value="inputValue"
        class="amt__input"
        type="text"
        inputmode="decimal"
        :aria-label="`Amount in ${currency}`"
        @input="onInput"
        @blur="onBlur"
      />

      <Box v-if="stepper" direction="col" gap="2px" class="amt__stepper">
        <Box
          is="button"
          flex
          align="center"
          justify="center"
          class="amt__step"
          type="button"
          aria-label="Increase amount"
          @click="increment"
        >
          <PiIcon name="chevron-up" class="amt__step-icon" />
        </Box>
        <Box
          is="button"
          flex
          align="center"
          justify="center"
          class="amt__step"
          type="button"
          aria-label="Decrease amount"
          @click="decrement"
        >
          <PiIcon name="chevron-down" class="amt__step-icon" />
        </Box>
      </Box>
    </Box>

    <Text v-if="error" type="body" color="danger" is="p"><CurrencyText :text="error" /></Text>

    <Box v-if="presets.length" gap="2" class="amt__presets">
      <Box
        v-for="preset in presets"
        :key="preset"
        is="button"
        flex
        align="center"
        justify="center"
        :class="[
          'amt__preset',
          { 'amt__preset--active': preset === modelValue && !isPresetDisabled(preset) },
        ]"
        type="button"
        :disabled="isPresetDisabled(preset)"
        :aria-pressed="preset === modelValue"
        @click="setAmount(preset)"
      >
        <Text type="body" is="span"><CurrencyText :text="`${currency} ${preset}`" /></Text>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.amt__box {
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-300);
  border-radius: 12px;
  padding: 0.875rem 0.875rem 0.875rem 1.125rem;
  box-sizing: border-box;
}

.amt__box:focus-within {
  border-color: var(--p-primary-600);
}

/* Listed after the focus rule, and matching focus too, so an invalid amount
   stays visibly red while the investor is still typing in the field. */
.amt__box--invalid,
.amt__box--invalid:focus-within {
  border-color: var(--p-danger-600);
}

.amt__currency {
  flex-shrink: 0;
}

/* Native input — the only place in this module that sets type directly. */
.amt__input {
  flex: 1;
  width: 0;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--p-text-color);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  font-family: inherit;
}

.amt__stepper {
  flex-shrink: 0;
}

.amt__step {
  padding: 0;
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: var(--p-text-muted-color);
  transition: color 0.15s;
}

.amt__step:hover {
  color: var(--p-text-color);
}

.amt__step:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.amt__step-icon {
  font-size: 20px;
}

.amt__preset {
  flex: 1;
  min-width: 0;
  min-height: 37px;
  padding: 9px 16px;
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-300);
  border-radius: 40px;
  color: var(--p-text-muted-color);
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;
}

.amt__preset:hover:not(:disabled) {
  background: var(--p-primary-50);
  border-color: var(--p-primary-200);
}

.amt__preset:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.amt__preset--active {
  background: var(--p-primary-50);
  border-color: var(--p-primary-600);
  color: var(--p-primary-700);
}

.amt__preset:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (max-width: 992px) {
  .amt__presets {
    flex-wrap: wrap;
  }

  .amt__preset {
    flex: 1 1 calc(50% - 0.25rem);
  }
}
</style>
